import { Badge, Button, Group, NumberInput, Paper, Select, SimpleGrid, Stack, Switch, Tabs, Text, TextInput, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useRates } from '@/api/catalog';
import { useDeleteWorkspace, useFeatures, useUpdateFeatures, useUpdateWorkspace, type Feature, type WorkspaceSettings } from '@/api/workspace';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { RateField } from '@/components/RateField';
import { useApiError } from '@/hooks/useApiError';
import dayjs from '@/lib/dayjs';
import { ActivitySettings } from './ActivitySettings';
import { BrandingSettings } from './BrandingSettings';
import { CustomFieldsSettings } from './CustomFieldsSettings';
import { ImportExportSettings } from './ImportExportSettings';
import { IntegrationsSettings } from './IntegrationsSettings';
import { SecuritySettings } from './SecuritySettings';

type Audience = WorkspaceSettings['permissions']['seeAmounts'];

/** Workspace-Einstellungen (WS-01…06). */
export function SettingsPage() {
  const { t } = useTranslation();
  const { tab = 'general' } = useParams();
  const navigate = useNavigate();
  const ctx = useCurrentWorkspace();
  return (
    <>
      <PageHeader title={t('nav.items.settings')} />
      <Tabs value={tab} onChange={(v) => navigate(`/settings/${v}`)} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="general">{t('settings.tabs.general')}</Tabs.Tab>
          <Tabs.Tab value="tracking">{t('settings.tabs.tracking')}</Tabs.Tab>
          <Tabs.Tab value="permissions">{t('settings.tabs.permissions')}</Tabs.Tab>
          <Tabs.Tab value="modules">{t('settings.tabs.modules')}</Tabs.Tab>
          <Tabs.Tab value="security">{t('settings.tabs.security')}</Tabs.Tab>
          <Tabs.Tab value="branding">{t('settings.tabs.branding')}</Tabs.Tab>
          {ctx.hasFeature('CUSTOM_FIELDS') && <Tabs.Tab value="fields">{t('settings.tabs.fields')}</Tabs.Tab>}
          {ctx.hasFeature('ACTIVITY') && <Tabs.Tab value="activity">{t('settings.tabs.activity')}</Tabs.Tab>}
          <Tabs.Tab value="import">{t('settings.tabs.import')}</Tabs.Tab>
          <Tabs.Tab value="integrations">{t('settings.tabs.integrations')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="general">
          <GeneralSettings />
        </Tabs.Panel>
        <Tabs.Panel value="tracking">
          <TrackingSettings />
        </Tabs.Panel>
        <Tabs.Panel value="permissions">
          <PermissionSettings />
        </Tabs.Panel>
        <Tabs.Panel value="modules">
          <ModuleSettings />
        </Tabs.Panel>
        <Tabs.Panel value="security">
          <SecuritySettings />
        </Tabs.Panel>
        <Tabs.Panel value="branding">
          <BrandingSettings />
        </Tabs.Panel>
        <Tabs.Panel value="fields">
          <CustomFieldsSettings />
        </Tabs.Panel>
        <Tabs.Panel value="activity">
          <ActivitySettings />
        </Tabs.Panel>
        <Tabs.Panel value="import">
          <ImportExportSettings />
        </Tabs.Panel>
        <Tabs.Panel value="integrations">
          <IntegrationsSettings />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

function useSettingsSaver() {
  const ctx = useCurrentWorkspace();
  const update = useUpdateWorkspace(ctx.workspaceId);
  const onError = useApiError();
  const saveSettings = (settings: WorkspaceSettings) => update.mutate({ settings }, { onError });
  return { ctx, update, saveSettings, onError };
}

function GeneralSettings() {
  const { t } = useTranslation();
  const { ctx, update, onError } = useSettingsSaver();
  const { data: rates = [] } = useRates(ctx.workspaceId);
  const remove = useDeleteWorkspace(ctx.workspaceId);
  const [name, setName] = useState(ctx.workspace.name);
  const [currency, setCurrency] = useState(ctx.workspace.currency);
  useEffect(() => {
    setName(ctx.workspace.name);
    setCurrency(ctx.workspace.currency);
  }, [ctx.workspace.name, ctx.workspace.currency]);

  return (
    <Stack maw={720}>
      <Paper withBorder p="md">
        <Stack>
          <TextInput label={t('workspace.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <Select
            label={t('settings.currency')}
            data={['EUR', 'CHF', 'USD', 'GBP', 'PLN', 'CZK', 'SEK', 'DKK', 'NOK']}
            value={currency}
            onChange={(v) => v && setCurrency(v)}
            searchable
          />
          <Group>
            <Button onClick={() => update.mutate({ name, currency }, { onError })} loading={update.isPending}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Paper>
      <Paper withBorder p="md">
        <Title order={5} mb="xs">
          {t('rates.workspaceRate')}
        </Title>
        <Text size="sm" c="dimmed" mb="sm">
          {t('rates.workspaceRateHint')}
        </Text>
        <RateField rates={rates} kind="BILLABLE" level="WORKSPACE" />
        {ctx.hasFeature('COST_RATES') && (
          <>
            <Text size="sm" fw={500} mt="md" mb={4}>
              {t('rates.costRate')}
            </Text>
            <RateField rates={rates} kind="COST" level="WORKSPACE" />
          </>
        )}
      </Paper>
      {ctx.isOwner && (
        <Paper withBorder p="md" style={{ borderColor: 'var(--mantine-color-red-4)' }}>
          <Group justify="space-between">
            <div>
              <Text fw={500}>{t('settings.deleteWorkspace')}</Text>
              <Text size="sm" c="dimmed">
                {t('settings.deleteWorkspaceHint')}
              </Text>
            </div>
            <Button
              color="red"
              variant="light"
              onClick={() =>
                modals.openConfirmModal({
                  title: t('settings.deleteWorkspace'),
                  children: <Text size="sm">{t('settings.deleteWorkspaceConfirm', { name: ctx.workspace.name })}</Text>,
                  labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(undefined, { onError }),
                })
              }
            >
              {t('common.delete')}
            </Button>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}

function TrackingSettings() {
  const { t } = useTranslation();
  const { ctx, saveSettings } = useSettingsSaver();
  const s = ctx.workspace.settings;
  const tr = s.tracking;
  const set = (changes: Partial<typeof tr>) => saveSettings({ ...s, tracking: { ...tr, ...changes } });
  const requiredEnabled = ctx.hasFeature('REQUIRED_FIELDS');

  return (
    <Stack maw={720}>
      <Paper withBorder p="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>{t('settings.requiredFields')}</Title>
          {!requiredEnabled && <Badge color="gray">{t('settings.notInPlan')}</Badge>}
        </Group>
        <SimpleGrid cols={2}>
          <Switch label={t('picker.project')} checked={tr.requireProject} disabled={!requiredEnabled} onChange={(e) => set({ requireProject: e.currentTarget.checked })} />
          <Switch label={t('settings.task')} checked={tr.requireTask} disabled={!requiredEnabled} onChange={(e) => set({ requireTask: e.currentTarget.checked })} />
          <Switch label={t('tracker.tags')} checked={tr.requireTag} disabled={!requiredEnabled} onChange={(e) => set({ requireTag: e.currentTarget.checked })} />
          <Switch label={t('tracker.description')} checked={tr.requireDescription} disabled={!requiredEnabled} onChange={(e) => set({ requireDescription: e.currentTarget.checked })} />
        </SimpleGrid>
      </Paper>
      <Paper withBorder p="md">
        <Stack>
          <Switch label={t('settings.defaultBillable')} checked={tr.defaultBillable} onChange={(e) => set({ defaultBillable: e.currentTarget.checked })} />
          <Switch label={t('settings.forbidFuture')} checked={tr.forbidFutureEntries} onChange={(e) => set({ forbidFutureEntries: e.currentTarget.checked })} />
          <Switch label={t('settings.breaksEnabled')} checked={tr.breaksEnabled} onChange={(e) => set({ breaksEnabled: e.currentTarget.checked })} />
          <Switch
            label={t('settings.overtimeTagging')}
            description={t('settings.overtimeTaggingHint')}
            checked={tr.overtimeTagging}
            onChange={(e) => set({ overtimeTagging: e.currentTarget.checked })}
          />
          <NumberInput
            label={t('settings.maxEntryHours')}
            description={t('settings.emptyNoLimit')}
            min={1}
            max={168}
            value={tr.maxEntryHours ?? ''}
            onBlur={(e) => set({ maxEntryHours: e.currentTarget.value ? Number(e.currentTarget.value) : undefined })}
            w={240}
          />
          <NumberInput
            label={t('settings.autoStopHours')}
            description={t('settings.emptyNoLimit')}
            min={1}
            max={168}
            value={tr.autoStopTimerHours ?? ''}
            onBlur={(e) => set({ autoStopTimerHours: e.currentTarget.value ? Number(e.currentTarget.value) : undefined })}
            w={240}
          />
        </Stack>
      </Paper>
      {ctx.hasFeature('APPROVALS') && (
        <Paper withBorder p="md">
          <Title order={5} mb="sm">
            {t('settings.approvals')}
          </Title>
          <Stack>
            <Select
              label={t('settings.approvalPeriod')}
              data={(['WEEKLY', 'SEMI_MONTHLY', 'MONTHLY'] as const).map((p) => ({ value: p, label: t(`settings.approvalPeriods.${p}`) }))}
              value={s.approvals.period}
              onChange={(v) => v && saveSettings({ ...s, approvals: { ...s.approvals, period: v as 'WEEKLY' } })}
              allowDeselect={false}
              w={240}
            />
            <Switch
              label={t('settings.teamManagersApprove')}
              checked={s.approvals.teamManagersApprove}
              onChange={(e) => saveSettings({ ...s, approvals: { ...s.approvals, teamManagersApprove: e.currentTarget.checked } })}
            />
            <Switch
              label={t('settings.projectManagersApprove')}
              checked={s.approvals.projectManagersApprove}
              onChange={(e) => saveSettings({ ...s, approvals: { ...s.approvals, projectManagersApprove: e.currentTarget.checked } })}
            />
            {ctx.hasFeature('EXPENSES') && (
              <Switch
                label={t('settings.approvalsIncludeExpenses')}
                checked={s.approvals.includeExpenses}
                onChange={(e) => saveSettings({ ...s, approvals: { ...s.approvals, includeExpenses: e.currentTarget.checked } })}
              />
            )}
          </Stack>
        </Paper>
      )}
      <Paper withBorder p="md">
        <Group justify="space-between" mb="sm">
          <Title order={5}>{t('settings.lockEntries')}</Title>
          {!ctx.hasFeature('LOCK_ENTRIES') && <Badge color="gray">{t('settings.notInPlan')}</Badge>}
        </Group>
        <Stack>
          <Select
            data={[
              { value: 'NONE', label: t('settings.lockNone') },
              { value: 'FIXED_DATE', label: t('settings.lockFixed') },
              { value: 'ROLLING_DAYS', label: t('settings.lockRolling') },
            ]}
            value={tr.lockMode}
            onChange={(v) => v && set({ lockMode: v as typeof tr.lockMode })}
            disabled={!ctx.hasFeature('LOCK_ENTRIES')}
            w={320}
          />
          {tr.lockMode === 'FIXED_DATE' && (
            <DatePickerInput
              label={t('settings.lockBefore')}
              value={tr.lockDate ?? null}
              onChange={(v) => set({ lockDate: v ? dayjs(v).format('YYYY-MM-DD') : undefined })}
              w={240}
            />
          )}
          {tr.lockMode === 'ROLLING_DAYS' && (
            <NumberInput
              label={t('settings.lockDays')}
              min={1}
              value={tr.lockDays ?? 7}
              onBlur={(e) => set({ lockDays: Number(e.currentTarget.value) || 7 })}
              w={240}
            />
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

function PermissionSettings() {
  const { t } = useTranslation();
  const { ctx, saveSettings } = useSettingsSaver();
  const s = ctx.workspace.settings;
  const p = s.permissions;
  const set = (changes: Partial<typeof p>) => saveSettings({ ...s, permissions: { ...p, ...changes } });
  const audienceData = [
    { value: 'ADMINS', label: t('settings.audience.ADMINS') },
    { value: 'MANAGERS', label: t('settings.audience.MANAGERS') },
    { value: 'EVERYONE', label: t('settings.audience.EVERYONE') },
  ];
  const row = (label: string, key: 'createProjects' | 'createClients' | 'createTags' | 'createTasks' | 'seeAmounts') => (
    <Group justify="space-between">
      <Text size="sm">{label}</Text>
      <Select data={audienceData} value={p[key]} onChange={(v) => v && set({ [key]: v as Audience })} w={220} />
    </Group>
  );
  return (
    <Paper withBorder p="md" maw={720}>
      <Stack>
        {row(t('settings.whoCreatesProjects'), 'createProjects')}
        {row(t('settings.whoCreatesClients'), 'createClients')}
        {row(t('settings.whoCreatesTags'), 'createTags')}
        {row(t('settings.whoCreatesTasks'), 'createTasks')}
        {row(t('settings.whoSeesAmounts'), 'seeAmounts')}
        <Switch label={t('settings.everyoneSeesAllReports')} checked={p.everyoneSeesAllReports} onChange={(e) => set({ everyoneSeesAllReports: e.currentTarget.checked })} />
        <Switch label={t('settings.everyoneSeesTeamDashboard')} checked={p.everyoneSeesTeamDashboard} onChange={(e) => set({ everyoneSeesTeamDashboard: e.currentTarget.checked })} />
      </Stack>
    </Paper>
  );
}

function ModuleSettings() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data } = useFeatures(ctx.workspaceId);
  const update = useUpdateFeatures(ctx.workspaceId);
  const onError = useApiError();
  if (!data) return null;
  return (
    <Stack maw={720}>
      <Paper withBorder p="md">
        <Group justify="space-between">
          <Text>
            {t('settings.currentPlan')}: <b>{data.planName}</b>
          </Text>
        </Group>
      </Paper>
      <Paper withBorder p="md">
        <Stack>
          {data.features
            .filter((f) => f.toggleable)
            .map((f) => (
              <Group key={f.feature} justify="space-between">
                <div>
                  <Text size="sm" fw={500}>
                    {t(`features.${f.feature}`)}
                  </Text>
                  {!f.inPlan && (
                    <Text size="xs" c="dimmed">
                      {t('settings.notInPlan')}
                    </Text>
                  )}
                </div>
                <Switch
                  checked={f.enabled}
                  disabled={!f.inPlan}
                  onChange={(e) => update.mutate({ [f.feature]: e.currentTarget.checked } as Partial<Record<Feature, boolean>>, { onError })}
                  aria-label={t(`features.${f.feature}`)}
                />
              </Group>
            ))}
        </Stack>
      </Paper>
    </Stack>
  );
}
